import React from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, intl, LOCALES_FLAG, DmsTextField, DmsTextFieldPhone, DmsTextFieldEmail } from "dmsbasereact";
import { DmsTextFieldCpf } from "../DmsTextFieldCpf/DmsTextFieldCpf";
import { DmsTextFieldCep } from "../DmsTextFieldCep/DmsTextFieldCep";
import {
    Box, useMediaQuery, useTheme, Alert, Grid,
    FormControl, InputLabel, Select, MenuItem, OutlinedInput,
    Checkbox, ListItemText, Chip, Typography
} from "@mui/material";
import { STATUS_OPTIONS } from "../../constants/UserManagementConstants";
import { getBrazilianStateOptions } from "../../constants/BrazilianStates";
import { PasswordValidation } from "../PasswordValidation/PasswordValidation";
import { MultiCrmInput } from "../MultiCrmInput";
import { useUserForm } from "./useUserForm";
import { DmsInstitutionSelect } from "../DmsInstitutionSelect";
import { InstitutionSetupModal } from "../InstitutionSetupModal";
import { InstitutionCategoryEnum } from "../../constants/InstitutionType";
import { mapRolesToUserType } from "../../constants/UserType";

const UserFormModal = ({
    isVisible,
    onClose,
    onSave,
    user = null,
    availableRoles = [],
    loadingRoles = false,
    userType = null,
}) => {
    const {
        isEditMode,
        formName, setFormName,
        formEmail, setFormEmail,
        formPhone, setFormPhone,
        formLanguage, setFormLanguage,
        formStatus, setFormStatus,
        formRoles, setFormRoles,
        formPassword, setFormPassword,
        formPasswordConfirm, setFormPasswordConfirm,
        formCpf, setFormCpf,
        setCpfIsValid,
        formCrms, setFormCrms,
        formCrf, setFormCrf,
        formCrfState, setFormCrfState,
        formInstitution, setFormInstitution,
        formZipCode, setFormZipCode,
        formAddress, setFormAddress,
        formAddressNumber, setFormAddressNumber,
        formCity, setFormCity,
        formAddressState, setFormAddressState,
        institutionAddress,
        needsPersonFields,
        needsDoctorFields,
        needsPharmacistFields,
        needsInstitutionField,
        needsAddressFields,
        showPasswordField,
        handleSave,
        title,
        helperText,
        institutionModalVisible,
        setInstitutionModalVisible,
        handleInstitutionSave,
        createdInstitution,
        fieldErrors,
    } = useUserForm({ isVisible, onSave, user, availableRoles, userType });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
        >
            <DmsModal.Header onClose={onClose} title={title} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {helperText && (
                        <Alert severity="info" sx={{ mb: 3, fontSize: "0.875rem" }}>
                            {helperText}
                        </Alert>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("BasicInfo").d("Basic Information")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldEmail
                                label={intl.get("Email").d("Email")}
                                value={formEmail}
                                onChange={(e) => setFormEmail(e.target.value)}
                                fullWidth
                                required
                                disabled={isEditMode}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextField
                                name="name"
                                label={intl.get("Name").d("Name")}
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                fullWidth
                                required
                                error={!!fieldErrors.name}
                                helperText={fieldErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldPhone
                                name="phone"
                                label={intl.get("Phone").d("Phone")}
                                value={formPhone}
                                onChange={(e) => setFormPhone(e.target.value)}
                                maskFunction={(value) => value}
                                fullWidth
                                required
                                error={!!fieldErrors.phone}
                                helperText={fieldErrors.phone}
                            />
                        </Grid>
                        {showPasswordField && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextField
                                        name="password"
                                        label={intl.get("Password").d("Password")}
                                        value={formPassword}
                                        onChange={(e) => setFormPassword(e.target.value)}
                                        fullWidth
                                        type="password"
                                        error={!!fieldErrors.password}
                                        helperText={fieldErrors.password || intl.get("PasswordHelperText").d("Leave empty to auto-generate a password")}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextField
                                        name="passwordConfirm"
                                        label={intl.get("ConfirmPassword").d("Confirm Password")}
                                        value={formPasswordConfirm}
                                        onChange={(e) => setFormPasswordConfirm(e.target.value)}
                                        fullWidth
                                        type="password"
                                        disabled={!formPassword}
                                        error={!!fieldErrors.passwordConfirm}
                                        helperText={fieldErrors.passwordConfirm}
                                    />
                                </Grid>
                                {formPassword && (
                                    <Grid item xs={12}>
                                        <PasswordValidation
                                            password={formPassword}
                                            confirmPassword={formPasswordConfirm}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{intl.get("Language").d("Language")}</InputLabel>
                                <Select
                                    value={formLanguage}
                                    label={intl.get("Language").d("Language")}
                                    onChange={(e) => setFormLanguage(e.target.value)}
                                >
                                    {Object.entries(LOCALES_FLAG).map(([label, value]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{intl.get("Status").d("Status")}</InputLabel>
                                <Select
                                    value={formStatus}
                                    label={intl.get("Status").d("Status")}
                                    onChange={(e) => setFormStatus(e.target.value)}
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {intl.get(opt.label).d(opt.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {needsPersonFields && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("PersonInfo").d("Person Information")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextFieldCpf
                                        label={intl.get("CPF").d("CPF")}
                                        value={formCpf}
                                        onChange={(e) => setFormCpf(e.target.value)}
                                        onValidationChange={setCpfIsValid}
                                        fullWidth
                                        required
                                        disabled={isEditMode}
                                        error={!!fieldErrors.cpf}
                                        helperText={fieldErrors.cpf}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {needsAddressFields && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("AddressInfo").d("Address Information")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <DmsTextFieldCep
                                        label={intl.get("ZipCode").d("Zip Code")}
                                        value={formZipCode}
                                        onChange={(e) => setFormZipCode(e.target.value)}
                                        fullWidth
                                        required
                                        error={!!fieldErrors.zipCode}
                                        helperText={fieldErrors.zipCode}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <DmsTextField
                                        name="address"
                                        label={intl.get("Address").d("Address")}
                                        value={formAddress}
                                        onChange={(e) => setFormAddress(e.target.value)}
                                        fullWidth
                                        required
                                        error={!!fieldErrors.address}
                                        helperText={fieldErrors.address}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <DmsTextField
                                        name="addressNumber"
                                        label={intl.get("Number").d("Number")}
                                        value={formAddressNumber}
                                        onChange={(e) => setFormAddressNumber(e.target.value)}
                                        fullWidth
                                        required
                                        error={!!fieldErrors.addressNumber}
                                        helperText={fieldErrors.addressNumber}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <DmsTextField
                                        name="city"
                                        label={intl.get("City").d("City")}
                                        value={formCity}
                                        onChange={(e) => setFormCity(e.target.value)}
                                        fullWidth
                                        required
                                        error={!!fieldErrors.city}
                                        helperText={fieldErrors.city}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth required error={!!fieldErrors.addressState}>
                                        <InputLabel>{intl.get("State").d("State")}</InputLabel>
                                        <Select
                                            value={formAddressState}
                                            label={intl.get("State").d("State")}
                                            onChange={(e) => setFormAddressState(e.target.value)}
                                        >
                                            {getBrazilianStateOptions().map(({ value, label }) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                        {fieldErrors.addressState && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                {fieldErrors.addressState}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {needsInstitutionField && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("Institution").d("Institution")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <DmsInstitutionSelect
                                        value={formInstitution}
                                        onChange={(value) => setFormInstitution(value)}
                                        userType={userType}
                                        multiple={userType === "doctor" || userType === "person" || userType === "distributor_responsible"}
                                        category={userType === "distributor_responsible" ? InstitutionCategoryEnum.Distributor : null}
                                        requireApproved={true}
                                        customOptions={[
                                            ...(createdInstitution ? [{
                                                value: createdInstitution.organization_branch_pk,
                                                label: createdInstitution.name,
                                            }] : []),
                                        ]}
                                        showCreateButton={true}
                                        onCreateInstitution={() => setInstitutionModalVisible(true)}
                                    />
                                </Grid>
                                {needsPharmacistFields && institutionAddress && (
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {intl.get("InstitutionAddress").d("Institution Address")}
                                            </Typography>
                                            <Typography variant="body2">
                                                {institutionAddress.name}
                                            </Typography>
                                            {institutionAddress.address && (
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    {institutionAddress.address.address && `${institutionAddress.address.address}, `}
                                                    {institutionAddress.address.number && `${institutionAddress.address.number} - `}
                                                    {institutionAddress.address.city && `${institutionAddress.address.city}, `}
                                                    {institutionAddress.address.state}
                                                    {institutionAddress.address.zip_code && ` - CEP: ${institutionAddress.address.zip_code}`}
                                                </Typography>
                                            )}
                                        </Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}

                    {needsDoctorFields && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("DoctorInfo").d("Doctor Information")}
                            </Typography>
                            <MultiCrmInput
                                crms={formCrms}
                                onChange={setFormCrms}
                                required={!isEditMode}
                                disabled={false}
                            />
                            {(fieldErrors.crm || fieldErrors.crmState) && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {fieldErrors.crm || fieldErrors.crmState}
                                </Alert>
                            )}
                        </>
                    )}

                    {needsPharmacistFields && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("PharmacistInfo").d("Pharmacist Information")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextField
                                        name="crf"
                                        label={intl.get("CRF").d("CRF")}
                                        value={formCrf}
                                        onChange={(e) => setFormCrf(e.target.value)}
                                        fullWidth
                                        required
                                        error={!!fieldErrors.crf}
                                        helperText={fieldErrors.crf}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required error={!!fieldErrors.crfState}>
                                        <InputLabel>{intl.get("CRFState").d("CRF State")}</InputLabel>
                                        <Select
                                            value={formCrfState}
                                            label={intl.get("CRFState").d("CRF State")}
                                            onChange={(e) => setFormCrfState(e.target.value)}
                                        >
                                            {getBrazilianStateOptions().map(({ value, label }) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                        {fieldErrors.crfState && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                {fieldErrors.crfState}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("Roles").d("Roles")}
                    </Typography>
                    <FormControl fullWidth disabled={loadingRoles}>
                        <InputLabel>{intl.get("Roles").d("Roles")}</InputLabel>
                        <Select
                            multiple
                            value={formRoles}
                            label={intl.get("Roles").d("Roles")}
                            onChange={(e) => setFormRoles(e.target.value)}
                            input={<OutlinedInput label={intl.get("Roles").d("Roles")} />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={intl.get(`RoleName.${value}`).d(value)} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {availableRoles.map((role) => (
                                <MenuItem key={role.pk} value={role.role_name}>
                                    <Checkbox checked={formRoles.indexOf(role.role_name) > -1} />
                                    <ListItemText primary={intl.get(`RoleName.${role.role_name}`).d(role.role_name)} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={onClose}
                            fullWidth={isMobile}
                        />
                        <DmsButton
                            variant="primary"
                            text={intl.get("Save").d("Save")}
                            onClick={handleSave}
                            fullWidth={isMobile}
                        />
                    </Box>
                </Box>
            </DmsModal.Footer>
            {/* Institution Creation Modal */}
            <InstitutionSetupModal
                isVisible={institutionModalVisible}
                onClose={() => setInstitutionModalVisible(false)}
                onSave={handleInstitutionSave}
                mode="create"
                publicMode={false}
                userType={mapRolesToUserType(formRoles)}
            />
        </DmsModal>
    );
};

UserFormModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    user: PropTypes.object,
    availableRoles: PropTypes.array,
    loadingRoles: PropTypes.bool,
    userType: PropTypes.oneOf(["sso", "person", "pharmacist", "doctor", "auditor", null]),
};


export { UserFormModal };
